import React from 'react';
import { Save, RotateCcw } from 'lucide-react';
import Editor from '@monaco-editor/react';

const getDefaultCalculationCode = () => {
  return `// ============================================
// VARIABLEN DEFINITION
// ============================================

// Preise (in EUR)
SWP_GRUNDPREIS = 10000
LWP_PREIS_PRO_M2 = 150
LWP_MINDESTPREIS = 8000
SEPARATUS_FAKTOR = 0.7

// Energie & Effizienz
STROMPREIS_PRO_KWH = 0.35
EFFIZIENZ_LUFTWAERME = 3.5
EFFIZIENZ_ERDWAERME = 4.5
GRUNDBEDARF_KWH_PRO_M2 = 50
WARMWASSER_KWH_PRO_PERSON = 800

// 25-Jahres-Faktoren
BETRIEBSJAHRE = 25
ERNEUERUNGSFAKTOR_SEPARATUS = 0.15
ERNEUERUNGSFAKTOR_KONVENTIONELLE = 0.25
ERNEUERUNGSFAKTOR_LWP = 0.40

// ============================================  
// BERECHNUNGSLOGIK
// ============================================

// 1. Wärmebedarf berechnen
WAERMEBEDARF_BERECHNET = WOHNFLAECHE * GRUNDBEDARF_KWH_PRO_M2 + PERSONEN * WARMWASSER_KWH_PRO_PERSON * WARMWASSER
WAERMEBEDARF = MAX(ENERGIEVERBRAUCH, WAERMEBEDARF_BERECHNET)

// 2. Leistung und Bohrung
LEISTUNGSBEDARF = WAERMEBEDARF / 2400
BOHRMETER = LEISTUNGSBEDARF / EFFIZIENZ_ERDWAERME * (EFFIZIENZ_ERDWAERME - 1) * 1000 / 35

// ============================================
// 3. INVESTITIONSKOSTEN (Zu Beginn)
// ============================================
INVEST_BEGINN_SEPARATUS = BOHRMETER * 95 + SWP_GRUNDPREIS
INVEST_BEGINN_KONVENTIONELLE = BOHRMETER * 117 + SWP_GRUNDPREIS
INVEST_BEGINN_LUFTWAERME = MAX(LWP_MINDESTPREIS, WOHNFLAECHE * LWP_PREIS_PRO_M2)

// ============================================
// 4. JÄHRLICHE BETRIEBSKOSTEN
// ============================================
JAHRESKOSTEN_SEPARATUS = WAERMEBEDARF / EFFIZIENZ_ERDWAERME * STROMPREIS_PRO_KWH
JAHRESKOSTEN_KONVENTIONELLE = WAERMEBEDARF / EFFIZIENZ_ERDWAERME * STROMPREIS_PRO_KWH
JAHRESKOSTEN_LUFTWAERME = WAERMEBEDARF / EFFIZIENZ_LUFTWAERME * STROMPREIS_PRO_KWH

// ============================================
// 5. BETRIEBSKOSTEN (25 Jahre)
// ============================================
BETRIEB25J_SEPARATUS = JAHRESKOSTEN_SEPARATUS * BETRIEBSJAHRE
BETRIEB25J_KONVENTIONELLE = JAHRESKOSTEN_KONVENTIONELLE * BETRIEBSJAHRE
BETRIEB25J_LUFTWAERME = JAHRESKOSTEN_LUFTWAERME * BETRIEBSJAHRE

// ============================================
// 6. ERNEUERUNGSKOSTEN
// ============================================
ERNEUERUNGSKOSTEN_SEPARATUS = INVEST_BEGINN_SEPARATUS * ERNEUERUNGSFAKTOR_SEPARATUS
ERNEUERUNGSKOSTEN_KONVENTIONELLE = INVEST_BEGINN_KONVENTIONELLE * ERNEUERUNGSFAKTOR_KONVENTIONELLE
ERNEUERUNGSKOSTEN_LUFTWAERME = INVEST_BEGINN_LUFTWAERME * ERNEUERUNGSFAKTOR_LWP

// ============================================
// 7. ANTEILIGE INVESTITIONSKOSTEN (für Balkendiagramm)
// ============================================
INVEST_ANTEIL_SEPARATUS = INVEST_BEGINN_SEPARATUS + ERNEUERUNGSKOSTEN_SEPARATUS
INVEST_ANTEIL_KONVENTIONELLE = INVEST_BEGINN_KONVENTIONELLE + ERNEUERUNGSKOSTEN_KONVENTIONELLE
INVEST_ANTEIL_LUFTWAERME = INVEST_BEGINN_LUFTWAERME + ERNEUERUNGSKOSTEN_LUFTWAERME

// ============================================
// 8. VOLLKOSTEN (Invest-Anteil + Betrieb 25J)
// ============================================
VOLLKOSTEN_SEPARATUS = INVEST_ANTEIL_SEPARATUS + BETRIEB25J_SEPARATUS
VOLLKOSTEN_KONVENTIONELLE = INVEST_ANTEIL_KONVENTIONELLE + BETRIEB25J_KONVENTIONELLE
VOLLKOSTEN_LUFTWAERME = INVEST_ANTEIL_LUFTWAERME + BETRIEB25J_LUFTWAERME

// ============================================
// 9. EINSPARUNG
// ============================================
ERSPARNIS_VOLLKOSTEN = VOLLKOSTEN_LUFTWAERME - VOLLKOSTEN_SEPARATUS`;
};

const CalculationLogicTab = ({ calculationCode, setCalculationCode, saveCalculationCode, onResetToDefault, t }) => {
  return (
    <>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>Berechnungslogik Editor (Vereinfacht)</h3>
      <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
        Definieren Sie hier die Berechnungslogik <strong>ohne Programmierung</strong>. Verwenden Sie einfache Variablen-Namen und normale Rechenzeichen.
      </p>
      
      <details style={{ marginBottom: '1.5rem', padding: '1rem', background: '#F5F5F5', borderRadius: '8px', border: '1px solid #ddd' }}>
        <summary style={{ fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', color: '#1976D2' }}>
          📖 Vollständige Dokumentation anzeigen
        </summary>
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', lineHeight: '1.6' }}>
          <h4 style={{ fontWeight: '600', marginTop: '1rem', marginBottom: '0.5rem' }}>Verfügbare Eingabevariablen:</h4>
          <ul style={{ marginLeft: '1.5rem', listStyleType: 'none' }}>
            <li><code>PLZ</code> - Postleitzahl</li>
            <li><code>ORT</code> - Ortsname</li>
            <li><code>WOHNFLAECHE</code> - Wohnfläche in m²</li>
            <li><code>PERSONEN</code> - Anzahl Personen</li>
            <li><code>WARMWASSER</code> - 1 für Ja, 0 für Nein</li>
            <li><code>ENERGIEVERBRAUCH</code> - Energieverbrauch in kWh</li>
            <li><code>MEERESHOEHE</code> - Höhe in Metern</li>
            <li><code>LAND</code> - CH, DE oder AT</li>
            <li><code>PROJEKTTYP</code> - neubau oder tausch-sanierung</li>
            <li><code>GEBAEUDETYP</code> - efh, mfh oder sonstiges</li>
            <li><code>FOERDERBETRAG_ERDWAERME</code> / <code>FOERDERBETRAG_LUFTWAERME</code></li>
          </ul>
          <h4 style={{ fontWeight: '600', marginTop: '1rem', marginBottom: '0.5rem' }}>Verfügbare Funktionen:</h4>
          <ul style={{ marginLeft: '1.5rem' }}>
            <li><code>MAX(a, b)</code> / <code>MIN(a, b)</code></li>
          </ul>
          <h4 style={{ fontWeight: '600', marginTop: '1rem', marginBottom: '0.5rem' }}>IF/THEN/ELSE:</h4>
          <pre style={{ background: 'white', padding: '0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>
{`IF PROJEKTTYP == neubau THEN
  FAKTOR = 1.0
ELSE
  FAKTOR = 1.2
ENDIF`}
          </pre>
        </div>
      </details>

      <div style={{ marginBottom: '1rem' }}>
        <button onClick={saveCalculationCode} className="btn-primary" style={{ marginRight: '0.5rem' }}>
          <Save size={16} style={{ marginRight: '0.5rem' }} /> Speichern
        </button>
        <button
          onClick={() => {
            const newCode = getDefaultCalculationCode();
            setCalculationCode(newCode);
            onResetToDefault(newCode);
          }}
          className="btn-secondary"
        >
          <RotateCcw size={16} style={{ marginRight: '0.5rem' }} /> Auf Standard zurücksetzen
        </button>
      </div>
      <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
        <Editor
          height="600px"
          defaultLanguage="ini"
          value={calculationCode}
          onChange={(value) => setCalculationCode(value || '')}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on'
          }}
        />
      </div>
    </>
  );
};

export default CalculationLogicTab;
